import { useState } from 'react';
import { createStripeCheckout, getPriceIdFromSupabase } from '../../../lib/stripeCheckout';

export default function AssinaturaMensal() {
  const [showModal, setShowModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'basico' | 'premium' | 'vip'>('premium');
  const [loadingCheckout, setLoadingCheckout] = useState(false);

  const plans = [
    {
      id: 'basico' as const,
      nome: 'Básico',
      preco: 29,
      descricao: 'Para quem quer manter a prática viva',
      beneficios: [
        '1 novo exercício mensal',
        'Check-in guiado mensal',
        'Conteúdos exclusivos',
        'Acesso à biblioteca de recursos',
        'Suporte por e-mail'
      ]
    },
    {
      id: 'premium' as const,
      nome: 'Premium',
      preco: 57,
      descricao: 'Transformação contínua e profunda',
      destaque: true,
      beneficios: [
        'Tudo do Básico',
        '3 novos exercícios mensais',
        'Check-ins semanais guiados',
        'Conteúdos exclusivos avançados',
        'Planejamento trimestral',
        'Acesso à comunidade privada',
        'Áudios de meditação mensais',
        'Suporte prioritário'
      ]
    },
    {
      id: 'vip' as const,
      nome: 'VIP',
      preco: 97,
      descricao: 'Experiência completa de evolução',
      beneficios: [
        'Tudo do Premium',
        'Exercícios ilimitados',
        'Check-ins personalizados',
        'Sessão mensal ao vivo',
        'Planejamento estratégico mensal',
        'Comunidade VIP exclusiva',
        'Biblioteca completa de áudios',
        'Conteúdos antecipados',
        'Suporte VIP 24/7',
        '20% desconto em produtos'
      ]
    }
  ];

  const handleAssinaturaCheckout = async (planId: 'basico' | 'premium' | 'vip') => {
    setLoadingCheckout(true);
    try {
      const priceId = await getPriceIdFromSupabase(`assinatura_${planId}`);
      const currentUrl = window.location.origin;
      const checkoutUrl = await createStripeCheckout({
        priceId,
        mode: 'subscription',
        successUrl: `${currentUrl}/planos/sucesso?plan=assinatura-${planId}`,
        cancelUrl: `${currentUrl}/planos?canceled=true`,
        metadata: { plan: `assinatura-${planId}`, type: 'subscription' },
      });
      window.location.href = checkoutUrl;
    } catch (error) {
      console.error('Erro ao processar assinatura:', error);
      alert('Erro ao processar assinatura. Por favor, tente novamente.');
      setLoadingCheckout(false);
    }
  };

  return (
    <>
      <section className="py-32 bg-gradient-to-b from-zinc-950 via-neutral-950 to-stone-950 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"></div>
        <div className="absolute bottom-40 left-20 w-96 h-96 bg-amber-500/5 rounded-full blur-[140px]"></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          {/* Header */}
          <div className="text-center mb-20">
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-16 h-px bg-amber-500/30"></div>
              <span className="font-['Montserrat'] text-xs tracking-[0.35em] uppercase text-amber-400/80">
                <i className="ri-vip-crown-line mr-2"></i>
                Assinatura Mensal
              </span>
              <div className="w-16 h-px bg-amber-500/30"></div>
            </div>
            <h2 className="font-['Playfair_Display'] text-5xl lg:text-6xl text-stone-50 mb-6 font-bold">
              Clube <span className="text-amber-400 italic">Eu do Futuro</span>
            </h2>
            <p className="font-['Montserrat'] text-lg text-stone-300 max-w-3xl mx-auto font-light leading-relaxed">
              Mantenha sua evolução constante com novos exercícios, check-ins guiados e uma comunidade de pessoas comprometidas com o crescimento pessoal.
            </p>
          </div>

          {/* Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative rounded-3xl p-8 transition-all duration-500 border-2 ${
                  plan.destaque
                    ? 'bg-gradient-to-br from-amber-950/50 to-yellow-950/40 border-amber-500/50 shadow-[0_0_60px_rgba(251,191,36,0.15)] scale-105'
                    : 'bg-gradient-to-br from-stone-900/50 to-zinc-900/50 border-stone-700/50 hover:border-amber-500/30 hover:-translate-y-1'
                }`}
              >
                {plan.destaque && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-stone-950 font-['Montserrat'] font-black text-xs px-6 py-2 rounded-full tracking-wider uppercase whitespace-nowrap shadow-xl">
                      <i className="ri-star-fill mr-1"></i>
                      Mais Popular
                    </div>
                  </div>
                )}

                <h3 className="font-['Playfair_Display'] text-3xl font-bold text-stone-50 mb-2 pt-2">
                  {plan.nome}
                </h3>
                <p className="font-['Montserrat'] text-sm text-stone-400 mb-8 tracking-wide h-12">
                  {plan.descricao}
                </p>

                <div className="mb-8 pb-8 border-b border-amber-500/10">
                  <div className="flex items-baseline gap-2">
                    <span className={`font-['Playfair_Display'] text-5xl font-bold ${plan.destaque ? 'text-amber-400' : 'text-stone-50'}`}>
                      R$ {plan.preco}
                    </span>
                    <span className="font-['Montserrat'] text-base text-stone-400">/mês</span>
                  </div>
                  <p className="font-['Montserrat'] text-sm text-stone-500 mt-2">Cancele quando quiser</p>
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.beneficios.map((b, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-xs text-amber-400"></i>
                      </div>
                      <span className="font-['Montserrat'] text-sm text-stone-300 leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => {
                    setSelectedPlan(plan.id);
                    setShowModal(true);
                  }}
                  className={`w-full py-4 rounded-xl font-['Montserrat'] font-bold text-sm transition-all whitespace-nowrap cursor-pointer tracking-wider uppercase ${
                    plan.destaque
                      ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-stone-950 hover:shadow-[0_0_40px_rgba(251,191,36,0.3)] hover:scale-105'
                      : 'border-2 border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/60'
                  }`}
                >
                  Assinar Agora
                </button>
              </div>
            ))}
          </div>

          {/* Garantia */}
          <div className="text-center">
            <div className="inline-flex items-center gap-4 bg-stone-900/40 rounded-xl px-8 py-4 border border-stone-700/50">
              <i className="ri-shield-check-line text-2xl text-amber-400"></i>
              <p className="font-['Montserrat'] text-base text-stone-300">
                <strong className="text-amber-400">Garantia de 7 dias</strong> — Experimente sem riscos
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-gradient-to-br from-stone-900 to-zinc-900 rounded-3xl max-w-lg w-full border-2 border-stone-700/50 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 border-b border-amber-500/10 relative">
              <button
                onClick={() => setShowModal(false)}
                className="absolute top-4 right-4 w-12 h-12 rounded-full bg-stone-800/50 hover:bg-stone-700/50 flex items-center justify-center transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-2xl text-stone-300"></i>
              </button>
              <h3 className="font-['Playfair_Display'] text-3xl font-bold text-stone-50 mb-1">
                <i className="ri-vip-crown-fill text-amber-400 mr-2"></i>
                Clube Eu do Futuro
              </h3>
              <p className="font-['Montserrat'] text-sm text-stone-400">
                {plans.find(p => p.id === selectedPlan)?.nome} — R$ {plans.find(p => p.id === selectedPlan)?.preco}/mês
              </p>
            </div>

            <div className="p-8">
              <div className="grid grid-cols-3 gap-3 mb-8">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`p-4 rounded-xl border-2 transition-all cursor-pointer ${
                      selectedPlan === plan.id
                        ? 'border-amber-500 bg-amber-500/10 scale-105'
                        : 'border-stone-700/50 hover:border-stone-600'
                    }`}
                  >
                    <div className={`font-['Montserrat'] text-sm font-bold mb-1 ${selectedPlan === plan.id ? 'text-amber-400' : 'text-stone-400'}`}>
                      {plan.nome}
                    </div>
                    <div className={`font-['Playfair_Display'] text-2xl font-bold ${selectedPlan === plan.id ? 'text-amber-400' : 'text-stone-50'}`}>
                      R$ {plan.preco}
                    </div>
                    <div className="font-['Montserrat'] text-xs text-stone-500">/mês</div>
                  </button>
                ))}
              </div>

              <div className="bg-stone-800/50 rounded-xl p-6 mb-8 border border-stone-700/50">
                <h4 className="font-['Montserrat'] text-sm font-bold text-amber-400 mb-4 uppercase tracking-wider">
                  Incluído:
                </h4>
                <ul className="space-y-2">
                  {plans.find(p => p.id === selectedPlan)?.beneficios.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="ri-check-line text-sm text-amber-400 mt-0.5"></i>
                      <span className="font-['Montserrat'] text-sm text-stone-300">{b}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button 
                onClick={() => handleAssinaturaCheckout(selectedPlan)}
                disabled={loadingCheckout}
                className="w-full py-5 bg-gradient-to-r from-amber-500 to-amber-600 text-stone-950 rounded-xl font-['Montserrat'] font-black text-base hover:shadow-[0_0_40px_rgba(251,191,36,0.3)] transition-all hover:scale-105 whitespace-nowrap cursor-pointer tracking-wider uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingCheckout ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    Processando...
                  </>
                ) : (
                  <>
                    Assinar por R$ {plans.find(p => p.id === selectedPlan)?.preco}/mês
                  </>
                )}
              </button>
              <p className="text-center font-['Montserrat'] text-xs text-stone-500 mt-4">
                <i className="ri-lock-line mr-1"></i> Pagamento seguro e criptografado
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
